import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../../layouts/DashboardLayout';
import {
  getWaAccounts, getWaConversations, getWaMessages,
  sendWaMessage, markWaConversationRead, closeWaConversation,
  getWaQuickReplies, getWaContact, updateWaContact,
  uploadWaMedia, getWaMediaUrl,
  getWaAgents, assignWaConversation, unassignWaConversation
} from '../../../services/api';
import { toast } from 'sonner';
import {
  MessageCircle, Send, ArrowLeft, Search, X, XCircle,
  CheckCheck, Check, Clock, Phone, User, Users, Settings,
  Zap, Image, FileText, Film, Mic, MapPin, Sticker,
  Inbox, Bot, Bell, List, ToggleLeft, Plus, Tag, StickyNote,
  Paperclip, AlertCircle, Download, UserCheck
} from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Cuentas', path: '/superadmin/whatsapp', icon: MessageCircle },
  { label: 'Inbox', path: '/superadmin/whatsapp/inbox', icon: Inbox },
  { label: 'Auto-respuestas', path: '/superadmin/whatsapp/auto-replies', icon: Bot },
  { label: 'Plantillas', path: '/superadmin/whatsapp/templates', icon: FileText },
  { label: 'Resp. Rapidas', path: '/superadmin/whatsapp/quick-replies', icon: Zap },
  { label: 'Contactos', path: '/superadmin/whatsapp/contacts', icon: Users },
  { label: 'Eventos', path: '/superadmin/whatsapp/event-settings', icon: Settings },
  { label: 'Notificaciones', path: '/superadmin/whatsapp/notifications', icon: Bell },
];

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'ahora';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
};

const MessageStatus = ({ msg }) => {
  const status = msg?.status;
  if (status === 'read') return <CheckCheck className="w-3.5 h-3.5 text-blue-500" />;
  if (status === 'delivered') return <CheckCheck className="w-3.5 h-3.5 text-slate-400" />;
  if (status === 'sent') return <Check className="w-3.5 h-3.5 text-slate-400" />;
  if (status === 'failed') return (
    <span className="relative group">
      <XCircle className="w-3.5 h-3.5 text-red-400" />
      {msg?.error_message && (
        <span className="absolute bottom-full right-0 mb-1 hidden group-hover:block bg-red-700 text-white text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap max-w-[200px] z-30">
          {msg.error_message}
        </span>
      )}
    </span>
  );
  return <Clock className="w-3.5 h-3.5 text-slate-300" />;
};

const MediaIcon = ({ type }) => {
  if (type === 'image') return <Image className="w-4 h-4" />;
  if (type === 'video') return <Film className="w-4 h-4" />;
  if (type === 'audio') return <Mic className="w-4 h-4" />;
  if (type === 'document') return <FileText className="w-4 h-4" />;
  if (type === 'location') return <MapPin className="w-4 h-4" />;
  if (type === 'sticker') return <Sticker className="w-4 h-4" />;
  return null;
};

const MediaBadge = ({ msg }) => {
  const media = msg.media;
  const type = msg.type;
  if (type === 'text' || type === 'template' || type === 'reaction') return null;

  const labels = {
    image: 'Imagen', video: 'Video', audio: 'Audio',
    document: 'Documento', location: 'Ubicacion', sticker: 'Sticker',
  };

  return (
    <div className="flex items-center gap-1.5 mb-1 opacity-70">
      <MediaIcon type={type} />
      <span className="text-[11px] font-medium">{labels[type] || type}</span>
      {media?.filename && <span className="text-[10px] opacity-75">({media.filename})</span>}
      {media?.mime_type && <span className="text-[10px] opacity-60">{media.mime_type}</span>}
    </div>
  );
};

// Interactive message rendering component
const InteractiveBadge = ({ msg }) => {
  const media = msg.media;
  if (!media) return null;

  if (media.interactive_type === 'button_reply') {
    return (
      <div className="flex items-center gap-1.5 mb-1">
        <ToggleLeft className="w-3.5 h-3.5 text-blue-500" />
        <span className="text-[11px] font-medium bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
          {media.button_title || media.button_id}
        </span>
      </div>
    );
  }

  if (media.interactive_type === 'list_reply') {
    return (
      <div className="flex items-center gap-1.5 mb-1">
        <List className="w-3.5 h-3.5 text-purple-500" />
        <span className="text-[11px] font-medium bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">
          {media.row_title || media.row_id}
        </span>
        {media.row_description && (
          <span className="text-[10px] text-slate-400">{media.row_description}</span>
        )}
      </div>
    );
  }

  return null;
};

// Interactive message modal for composing button/list messages
const InteractiveModal = ({ onClose, onSend }) => {
  const [tab, setTab] = useState('button'); // button | list
  const [bodyText, setBodyText] = useState('');
  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('');
  // Buttons
  const [buttons, setButtons] = useState([{ id: 'btn_1', title: '' }]);
  // List
  const [buttonText, setButtonText] = useState('Ver opciones');
  const [sections, setSections] = useState([{ title: 'Opciones', rows: [{ id: 'row_1', title: '', description: '' }] }]);

  const addButton = () => {
    if (buttons.length >= 3) return;
    setButtons([...buttons, { id: `btn_${buttons.length + 1}`, title: '' }]);
  };

  const updateButton = (idx, field, val) => {
    const updated = [...buttons];
    updated[idx][field] = val;
    setButtons(updated);
  };

  const removeButton = (idx) => {
    if (buttons.length <= 1) return;
    setButtons(buttons.filter((_, i) => i !== idx));
  };

  const addRow = (sIdx) => {
    const updated = [...sections];
    updated[sIdx].rows.push({ id: `row_${Date.now()}`, title: '', description: '' });
    setSections(updated);
  };

  const updateRow = (sIdx, rIdx, field, val) => {
    const updated = [...sections];
    updated[sIdx].rows[rIdx][field] = val;
    setSections(updated);
  };

  const removeRow = (sIdx, rIdx) => {
    const updated = [...sections];
    if (updated[sIdx].rows.length <= 1) return;
    updated[sIdx].rows = updated[sIdx].rows.filter((_, i) => i !== rIdx);
    setSections(updated);
  };

  const addSection = () => {
    setSections([...sections, { title: '', rows: [{ id: `row_${Date.now()}`, title: '', description: '' }] }]);
  };

  const handleSend = () => {
    if (!bodyText.trim()) return toast.error('El texto del mensaje es requerido');

    if (tab === 'button') {
      const validButtons = buttons.filter(b => b.title.trim());
      if (validButtons.length === 0) return toast.error('Agrega al menos un boton');
      onSend({
        message_type: 'button',
        body: bodyText.trim(),
        interactive_buttons: validButtons,
        interactive_header: headerText.trim() || undefined,
        interactive_footer: footerText.trim() || undefined,
      });
    } else {
      const validSections = sections.map(s => ({
        ...s,
        rows: s.rows.filter(r => r.title.trim()),
      })).filter(s => s.rows.length > 0);
      if (validSections.length === 0) return toast.error('Agrega al menos una opcion');
      if (!buttonText.trim()) return toast.error('El texto del boton es requerido');
      onSend({
        message_type: 'list',
        body: bodyText.trim(),
        interactive_button_text: buttonText.trim(),
        interactive_sections: validSections,
        interactive_header: headerText.trim() || undefined,
        interactive_footer: footerText.trim() || undefined,
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Mensaje interactivo</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          <button onClick={() => setTab('button')}
            className={`flex-1 py-2.5 text-sm font-medium text-center ${tab === 'button' ? 'text-green-700 border-b-2 border-green-500' : 'text-slate-500'}`}>
            Botones (max 3)
          </button>
          <button onClick={() => setTab('list')}
            className={`flex-1 py-2.5 text-sm font-medium text-center ${tab === 'list' ? 'text-green-700 border-b-2 border-green-500' : 'text-slate-500'}`}>
            Lista
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Common fields */}
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Encabezado (opcional)</label>
            <input value={headerText} onChange={e => setHeaderText(e.target.value)} maxLength={60}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Encabezado" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Mensaje *</label>
            <textarea value={bodyText} onChange={e => setBodyText(e.target.value)} rows={3} maxLength={1024}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none" placeholder="Texto del mensaje" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Pie de mensaje (opcional)</label>
            <input value={footerText} onChange={e => setFooterText(e.target.value)} maxLength={60}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Footer" />
          </div>

          {/* Button-specific */}
          {tab === 'button' && (
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-2">Botones</label>
              <div className="space-y-2">
                {buttons.map((btn, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={btn.title} onChange={e => updateButton(i, 'title', e.target.value)} maxLength={20}
                      className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm" placeholder={`Boton ${i + 1}`} />
                    {buttons.length > 1 && (
                      <button onClick={() => removeButton(i)} className="p-1 text-red-400 hover:text-red-600">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {buttons.length < 3 && (
                <button onClick={addButton} className="mt-2 text-xs text-green-600 hover:text-green-700 flex items-center gap-1">
                  <Plus className="w-3.5 h-3.5" /> Agregar boton
                </button>
              )}
            </div>
          )}

          {/* List-specific */}
          {tab === 'list' && (
            <>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Texto del boton *</label>
                <input value={buttonText} onChange={e => setButtonText(e.target.value)} maxLength={20}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Ver opciones" />
              </div>
              {sections.map((section, sIdx) => (
                <div key={sIdx} className="border border-slate-200 rounded-lg p-3 space-y-2">
                  <input value={section.title} onChange={e => {
                    const u = [...sections]; u[sIdx].title = e.target.value; setSections(u);
                  }} maxLength={24} className="w-full border border-slate-100 rounded px-2 py-1 text-sm font-medium" placeholder="Titulo seccion" />
                  {section.rows.map((row, rIdx) => (
                    <div key={rIdx} className="flex items-start gap-2 ml-2">
                      <div className="flex-1 space-y-1">
                        <input value={row.title} onChange={e => updateRow(sIdx, rIdx, 'title', e.target.value)} maxLength={24}
                          className="w-full border border-slate-100 rounded px-2 py-1 text-sm" placeholder="Titulo opcion" />
                        <input value={row.description} onChange={e => updateRow(sIdx, rIdx, 'description', e.target.value)} maxLength={72}
                          className="w-full border border-slate-100 rounded px-2 py-1 text-xs text-slate-500" placeholder="Descripcion (opcional)" />
                      </div>
                      {section.rows.length > 1 && (
                        <button onClick={() => removeRow(sIdx, rIdx)} className="p-1 text-red-400 hover:text-red-600 mt-0.5">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button onClick={() => addRow(sIdx)} className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 ml-2">
                    <Plus className="w-3 h-3" /> Opcion
                  </button>
                </div>
              ))}
              <button onClick={addSection} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Agregar seccion
              </button>
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
          <button onClick={handleSend} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">Enviar</button>
        </div>
      </div>
    </div>
  );
};

// Contact panel shown in chat sidebar
const ContactPanel = ({ phone, onClose }) => {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (!phone) return;
    setLoading(true);
    getWaContact(phone).then(r => {
      setContact(r.data?.contact || null);
      setNotes(r.data?.contact?.notes || '');
    }).catch(() => setContact(null)).finally(() => setLoading(false));
  }, [phone]);

  const saveNotes = async () => {
    if (!contact) return;
    try {
      await updateWaContact(contact.id, { notes });
      setEditingNotes(false);
      toast.success('Notas guardadas');
    } catch { toast.error('Error guardando notas'); }
  };

  const addTag = async () => {
    if (!contact || !newTag.trim()) return;
    const updatedTags = [...(contact.tags || []), newTag.trim()];
    try {
      await updateWaContact(contact.id, { tags: updatedTags });
      setContact({ ...contact, tags: updatedTags });
      setNewTag('');
    } catch { toast.error('Error agregando tag'); }
  };

  const removeTag = async (tag) => {
    if (!contact) return;
    const updatedTags = (contact.tags || []).filter(t => t !== tag);
    try {
      await updateWaContact(contact.id, { tags: updatedTags });
      setContact({ ...contact, tags: updatedTags });
    } catch { toast.error('Error removiendo tag'); }
  };

  if (loading) return (
    <div className="w-72 border-l border-slate-200 bg-white p-4 flex items-center justify-center">
      <span className="text-sm text-slate-400">Cargando...</span>
    </div>
  );

  if (!contact) return (
    <div className="w-72 border-l border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-slate-900">Contacto</h4>
        <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4 text-slate-400" /></button>
      </div>
      <p className="text-xs text-slate-400">Sin datos de contacto</p>
    </div>
  );

  return (
    <div className="w-72 border-l border-slate-200 bg-white flex flex-col overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-900">Contacto</h4>
        <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4 text-slate-400" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Info */}
        <div className="text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <User className="w-6 h-6 text-green-700" />
          </div>
          <p className="font-medium text-slate-900 text-sm">{contact.name}</p>
          <p className="text-xs text-slate-500">{contact.phone}</p>
          <p className="text-[10px] text-slate-400 mt-1">{contact.message_count || 0} mensajes</p>
        </div>

        {/* Tags */}
        <div>
          <div className="flex items-center gap-1 mb-2">
            <Tag className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-xs font-medium text-slate-600">Tags</span>
          </div>
          <div className="flex flex-wrap gap-1 mb-2">
            {(contact.tags || []).map(tag => (
              <span key={tag} className="inline-flex items-center gap-1 text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                {tag}
                <button onClick={() => removeTag(tag)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-1">
            <input value={newTag} onChange={e => setNewTag(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTag()}
              className="flex-1 text-xs border border-slate-200 rounded px-2 py-1" placeholder="Nuevo tag" />
            <button onClick={addTag} className="text-xs text-green-600 hover:text-green-700 px-1">
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Notes */}
        <div>
          <div className="flex items-center gap-1 mb-2">
            <StickyNote className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-xs font-medium text-slate-600">Notas</span>
          </div>
          {editingNotes ? (
            <div className="space-y-2">
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 resize-none" />
              <div className="flex gap-1">
                <button onClick={saveNotes} className="text-xs bg-green-600 text-white px-2 py-1 rounded">Guardar</button>
                <button onClick={() => { setEditingNotes(false); setNotes(contact.notes || ''); }} className="text-xs text-slate-500 px-2 py-1">Cancelar</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setEditingNotes(true)} className="w-full text-left">
              <p className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2 min-h-[32px] hover:bg-slate-100">
                {contact.notes || 'Click para agregar notas...'}
              </p>
            </button>
          )}
        </div>

        {/* Custom fields */}
        {contact.custom_fields && Object.keys(contact.custom_fields).length > 0 && (
          <div>
            <span className="text-xs font-medium text-slate-600 block mb-2">Campos personalizados</span>
            {Object.entries(contact.custom_fields).map(([k, v]) => (
              <div key={k} className="flex justify-between text-xs py-1 border-b border-slate-50">
                <span className="text-slate-500">{k}</span>
                <span className="text-slate-800 font-medium">{v}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const WhatsAppInbox = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  // Quick replies
  const [quickReplies, setQuickReplies] = useState([]);
  const [showQR, setShowQR] = useState(false);
  const qrRef = useRef(null);

  // Interactive modal
  const [showInteractive, setShowInteractive] = useState(false);

  // Contact panel
  const [showContact, setShowContact] = useState(false);

  // Media attachment
  const [attachedFile, setAttachedFile] = useState(null);
  const [attachedPreview, setAttachedPreview] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);

  // Multi-agent
  const [agents, setAgents] = useState([]);
  const [agentFilter, setAgentFilter] = useState('');

  // Load accounts, quick replies & agents once
  useEffect(() => {
    getWaAccounts().then(r => setAccounts(r.data?.accounts || [])).catch(() => {});
    getWaQuickReplies().then(r => setQuickReplies((r.data?.quick_replies || []).filter(qr => qr.is_active))).catch(() => {});
    getWaAgents().then(r => setAgents(r.data?.agents || [])).catch(() => {});
  }, []);

  // Close QR dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (qrRef.current && !qrRef.current.contains(e.target)) setShowQR(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const params = { limit: 100 };
      if (accountFilter) params.account_id = accountFilter;
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      if (agentFilter) params.assigned_to = agentFilter;
      const res = await getWaConversations(params);
      setConversations(res.data?.conversations || []);
    } catch { /* silent */ }
    finally { setLoadingConvs(false); }
  }, [accountFilter, statusFilter, search, agentFilter]);

  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    setLoadingMsgs(true);
    try {
      const res = await getWaMessages(convId, { limit: 200 });
      setMessages(res.data?.messages || []);
    } catch { toast.error('Error cargando mensajes'); }
    finally { setLoadingMsgs(false); }
  }, []);

  // Initial load + polling
  useEffect(() => {
    loadConversations();
    pollRef.current = setInterval(() => {
      loadConversations();
      if (selectedConv) loadMessages(selectedConv.id);
    }, 8000);
    return () => clearInterval(pollRef.current);
  }, [loadConversations, loadMessages, selectedConv]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const selectConversation = async (conv) => {
    setSelectedConv(conv);
    loadMessages(conv.id);
    if (conv.unread_count > 0) {
      try {
        await markWaConversationRead(conv.id);
        setConversations(prev =>
          prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c)
        );
      } catch { /* silent */ }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 16 * 1024 * 1024) {
      toast.error('El archivo excede 16MB');
      return;
    }
    setAttachedFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setAttachedPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setAttachedPreview(null);
    }
  };

  const clearAttachment = () => {
    setAttachedFile(null);
    setAttachedPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const _detectMessageType = (file) => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type.startsWith('audio/')) return 'audio';
    return 'document';
  };

  const handleSend = async () => {
    if ((!msgText.trim() && !attachedFile) || !selectedConv) return;
    setSending(true);
    try {
      let payload = {
        account_id: selectedConv.account_id,
        to: selectedConv.contact_phone,
      };

      if (attachedFile) {
        setUploadingFile(true);
        const uploadRes = await uploadWaMedia(attachedFile);
        setUploadingFile(false);
        const mediaUrl = uploadRes.data.url;
        // Convert relative to absolute URL if needed
        const absoluteUrl = mediaUrl.startsWith('http')
          ? mediaUrl
          : `${window.location.origin}${mediaUrl}`;
        const mediaType = _detectMessageType(attachedFile);
        payload.message_type = mediaType;
        payload.media_url = absoluteUrl;
        payload.media_caption = msgText.trim() || undefined;
        payload.media_filename = attachedFile.name;
      } else {
        payload.body = msgText.trim();
      }

      await sendWaMessage(payload);
      setMsgText('');
      clearAttachment();
      loadMessages(selectedConv.id);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error enviando mensaje');
    } finally {
      setSending(false);
      setUploadingFile(false);
    }
  };

  const handleSendInteractive = async (data) => {
    if (!selectedConv) return;
    setSending(true);
    try {
      await sendWaMessage({
        account_id: selectedConv.account_id,
        to: selectedConv.contact_phone,
        ...data,
      });
      setShowInteractive(false);
      loadMessages(selectedConv.id);
      loadConversations();
      toast.success('Mensaje interactivo enviado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error enviando mensaje interactivo');
    } finally { setSending(false); }
  };

  const handleClose = async () => {
    if (!selectedConv) return;
    if (!window.confirm('Cerrar esta conversacion?')) return;
    try {
      await closeWaConversation(selectedConv.id);
      toast.success('Conversacion cerrada');
      setSelectedConv(null);
      setMessages([]);
      loadConversations();
    } catch { toast.error('Error cerrando'); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickReply = (qr) => {
    setMsgText(qr.body);
    setShowQR(false);
  };

  const handleAssign = async (agentId) => {
    if (!selectedConv) return;
    try {
      if (!agentId) {
        await unassignWaConversation(selectedConv.id);
        setSelectedConv({ ...selectedConv, assigned_to: null, assigned_to_name: null });
      } else {
        const agent = agents.find(a => a.id === agentId);
        await assignWaConversation(selectedConv.id, { user_id: agentId, user_name: agent?.name || '' });
        setSelectedConv({ ...selectedConv, assigned_to: agentId, assigned_to_name: agent?.name || '' });
      }
      loadConversations();
    } catch { toast.error('Error asignando agente'); }
  };

  const getAccountName = (accountId) => {
    const acc = accounts.find(a => a.id === accountId);
    return acc?.display_name || '';
  };

  const totalUnread = conversations.reduce((s, c) => s + (c.unread_count || 0), 0);

  const getMediaSrc = (msg) => {
    // Outbound media has url directly
    if (msg.media?.url) return msg.media.url;
    // Inbound media: use proxy
    if (msg.media?.media_id && selectedConv?.account_id) {
      const token = localStorage.getItem('token');
      return getWaMediaUrl(msg.media.media_id, selectedConv.account_id) + `&token=${encodeURIComponent(token)}`;
    }
    return null;
  };

  const renderInlineMedia = (msg) => {
    const src = getMediaSrc(msg);
    if (!src) return null;

    if (msg.type === 'image') {
      return <img src={src} alt={msg.media?.caption || 'imagen'} className="max-w-full rounded-lg mb-1 max-h-64 object-cover cursor-pointer" onClick={() => window.open(src, '_blank')} />;
    }
    if (msg.type === 'video') {
      return <video src={src} controls className="max-w-full rounded-lg mb-1 max-h-64" />;
    }
    if (msg.type === 'audio') {
      return <audio src={src} controls className="w-full mb-1" />;
    }
    if (msg.type === 'document') {
      return (
        <a href={src} target="_blank" rel="noopener noreferrer"
          className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-1 text-xs ${
            msg.direction === 'outbound' ? 'bg-white/15 text-white hover:bg-white/25' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}>
          <Download className="w-4 h-4" />
          <span className="truncate">{msg.media?.filename || 'Descargar documento'}</span>
        </a>
      );
    }
    return null;
  };

  const renderMessage = (msg) => {
    const isInteractive = msg.media && (msg.media.interactive_type === 'button_reply' || msg.media.interactive_type === 'list_reply');
    const isOutboundInteractive = msg.direction === 'outbound' && (msg.type === 'button' || msg.type === 'list');
    const hasInlineMedia = ['image', 'video', 'audio', 'document'].includes(msg.type) && (msg.media?.url || msg.media?.media_id);

    return (
      <div key={msg.id} className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${
          msg.direction === 'outbound'
            ? 'bg-teal-600 text-white rounded-br-md'
            : 'bg-white text-slate-800 border border-slate-200 rounded-bl-md'
        }`}>
          {isInteractive && <InteractiveBadge msg={msg} />}
          {hasInlineMedia ? renderInlineMedia(msg) : (!isInteractive && <MediaBadge msg={msg} />)}
          {/* Show content unless it's just the placeholder like [Imagen] */}
          {msg.content && !(hasInlineMedia && /^\[(Imagen|Video|Audio|Documento)\]$/.test(msg.content)) && (
            <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
          )}
          {/* Render outbound button labels */}
          {isOutboundInteractive && msg.content && msg.content.includes('[Botones:') && (
            <div className="flex flex-wrap gap-1 mt-2">
              {msg.content.match(/\[Botones: (.+)\]/)?.[1]?.split(', ').map((label, i) => (
                <span key={i} className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full">{label}</span>
              ))}
            </div>
          )}
          <div className={`flex items-center justify-end gap-1 mt-1 ${
            msg.direction === 'outbound' ? 'text-teal-200' : 'text-slate-400'
          }`}>
            <span className="text-[10px]">
              {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : ''}
            </span>
            {msg.direction === 'outbound' && <MessageStatus msg={msg} />}
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Sub-nav */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 border-b border-slate-200 mb-4">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const active = item.path === '/superadmin/whatsapp/inbox';
            return (
              <button key={item.path} onClick={() => navigate(item.path)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  active ? 'bg-green-100 text-green-700 font-medium' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}>
                <Icon className="w-4 h-4" /> {item.label}
              </button>
            );
          })}
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-green-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">
                WhatsApp Inbox
                {totalUnread > 0 && (
                  <span className="ml-2 text-sm font-medium text-white bg-green-500 px-2 py-0.5 rounded-full">
                    {totalUnread}
                  </span>
                )}
              </h1>
              <p className="text-sm text-slate-500">{conversations.length} conversaciones</p>
            </div>
          </div>
        </div>

        {/* Split view */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex" style={{ height: 'calc(100vh - 220px)' }}>
          {/* Conversation list — left panel */}
          <div className={`w-full md:w-96 flex-shrink-0 border-r border-slate-200 flex flex-col ${selectedConv ? 'hidden md:flex' : 'flex'}`}>
            {/* Filters */}
            <div className="p-3 border-b border-slate-100 space-y-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Buscar contacto..."
                  className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                {search && (
                  <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <select value={accountFilter} onChange={e => setAccountFilter(e.target.value)}
                  className="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 focus:ring-2 focus:ring-green-500">
                  <option value="">Todas las cuentas</option>
                  {accounts.filter(a => a.status === 'active').map(a => (
                    <option key={a.id} value={a.id}>{a.display_name}</option>
                  ))}
                </select>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 focus:ring-2 focus:ring-green-500">
                  <option value="">Todos</option>
                  <option value="open">Abiertas</option>
                  <option value="closed">Cerradas</option>
                </select>
              </div>
              {agents.length > 0 && (
                <select value={agentFilter} onChange={e => setAgentFilter(e.target.value)}
                  className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 focus:ring-2 focus:ring-green-500">
                  <option value="">Todos los agentes</option>
                  <option value="__unassigned__">Sin asignar</option>
                  {agents.map(a => (
                    <option key={a.id} value={a.id}>{a.name || a.email}</option>
                  ))}
                </select>
              )}
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto">
              {loadingConvs ? (
                <div className="p-8 text-center text-slate-400 text-sm">Cargando...</div>
              ) : conversations.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <MessageCircle className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">Sin conversaciones</p>
                </div>
              ) : (
                conversations.map(conv => (
                  <button
                    key={conv.id}
                    onClick={() => selectConversation(conv)}
                    className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                      selectedConv?.id === conv.id ? 'bg-green-50 border-l-2 border-l-green-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900 text-sm truncate">{conv.contact_name}</p>
                          {conv.status === 'closed' && (
                            <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">cerrada</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{conv.contact_phone}</p>
                        <p className="text-xs text-slate-400 mt-1 truncate">{conv.last_message_preview}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className="text-[10px] text-slate-400">{timeAgo(conv.last_message_at)}</span>
                        {conv.unread_count > 0 && (
                          <span className="min-w-[20px] h-5 flex items-center justify-center text-[10px] font-bold text-white bg-green-500 rounded-full px-1.5">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {accounts.length > 1 && (
                        <span className="text-[10px] text-slate-400">{getAccountName(conv.account_id)}</span>
                      )}
                      {conv.assigned_to_name && (
                        <span className="text-[10px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                          <UserCheck className="w-3 h-3" /> {conv.assigned_to_name}
                        </span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Chat panel — right */}
          <div className={`flex-1 flex flex-col ${!selectedConv ? 'hidden md:flex' : 'flex'}`}>
            {!selectedConv ? (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 mx-auto mb-3 text-slate-200" />
                  <p className="font-medium">Selecciona una conversacion</p>
                  <p className="text-sm mt-1">Elige un chat de la lista para ver los mensajes</p>
                </div>
              </div>
            ) : (
              <>
                {/* Chat header */}
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-white">
                  <div className="flex items-center gap-3">
                    <button onClick={() => { setSelectedConv(null); setMessages([]); setShowContact(false); }}
                      className="md:hidden p-1 rounded-lg hover:bg-slate-100 text-slate-500">
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <button onClick={() => setShowContact(!showContact)}
                      className="w-9 h-9 bg-green-100 rounded-full flex items-center justify-center hover:bg-green-200 transition-colors"
                      title="Ver contacto">
                      <User className="w-4 h-4 text-green-700" />
                    </button>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{selectedConv.contact_name}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {selectedConv.contact_phone}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {agents.length > 0 && (
                      <select
                        value={selectedConv.assigned_to || ''}
                        onChange={e => handleAssign(e.target.value)}
                        className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 focus:ring-2 focus:ring-indigo-500 max-w-[140px]"
                        title="Asignar agente"
                      >
                        <option value="">Sin asignar</option>
                        {agents.map(a => (
                          <option key={a.id} value={a.id}>{a.name || a.email}</option>
                        ))}
                      </select>
                    )}
                    {selectedConv.status !== 'closed' && (
                      <button onClick={handleClose}
                        className="text-xs text-slate-500 hover:text-red-600 px-2 py-1 rounded-lg hover:bg-red-50">
                        Cerrar
                      </button>
                    )}
                  </div>
                </div>

                {/* Messages + optional contact panel */}
                <div className="flex-1 flex overflow-hidden">
                  {/* Messages */}
                  <div className="flex-1 flex flex-col">
                    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2 bg-slate-50">
                      {loadingMsgs ? (
                        <div className="text-center text-slate-400 text-sm py-8">Cargando mensajes...</div>
                      ) : messages.length === 0 ? (
                        <div className="text-center text-slate-400 text-sm py-8">Sin mensajes</div>
                      ) : (
                        messages.map(msg => renderMessage(msg))
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    {selectedConv.status !== 'closed' && (
                      <div className="border-t border-slate-200 bg-white">
                        {/* Attachment preview */}
                        {attachedFile && (
                          <div className="px-4 pt-3 pb-1 flex items-center gap-3">
                            {attachedPreview ? (
                              <img src={attachedPreview} alt="preview" className="w-16 h-16 object-cover rounded-lg border border-slate-200" />
                            ) : (
                              <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center">
                                {attachedFile.type.startsWith('video/') ? <Film className="w-6 h-6 text-slate-400" /> :
                                 attachedFile.type.startsWith('audio/') ? <Mic className="w-6 h-6 text-slate-400" /> :
                                 <FileText className="w-6 h-6 text-slate-400" />}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-slate-700 truncate">{attachedFile.name}</p>
                              <p className="text-[10px] text-slate-400">{(attachedFile.size / 1024).toFixed(0)} KB</p>
                            </div>
                            <button onClick={clearAttachment} className="p-1 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-red-500">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        <div className="px-4 py-3 flex items-end gap-2">
                          {/* Quick reply button */}
                          <div className="relative" ref={qrRef}>
                            <button
                              onClick={() => setShowQR(!showQR)}
                              className="p-2.5 text-amber-600 hover:bg-amber-50 rounded-xl flex-shrink-0"
                              title="Respuestas rapidas"
                            >
                              <Zap className="w-5 h-5" />
                            </button>
                            {showQR && quickReplies.length > 0 && (
                              <div className="absolute bottom-full left-0 mb-2 w-72 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto z-20">
                                {quickReplies.map(qr => (
                                  <button
                                    key={qr.id}
                                    onClick={() => handleQuickReply(qr)}
                                    className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-50 last:border-0"
                                  >
                                    <p className="text-sm font-medium text-slate-800">{qr.title}</p>
                                    <p className="text-xs text-slate-400 truncate">{qr.body}</p>
                                    {qr.shortcut && <span className="text-[10px] font-mono text-slate-400">{qr.shortcut}</span>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          {/* Interactive message button */}
                          <button
                            onClick={() => setShowInteractive(true)}
                            className="p-2.5 text-purple-600 hover:bg-purple-50 rounded-xl flex-shrink-0"
                            title="Mensaje interactivo (botones/lista)"
                          >
                            <ToggleLeft className="w-5 h-5" />
                          </button>
                          {/* File picker */}
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                            onChange={handleFileSelect}
                            className="hidden"
                          />
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="p-2.5 text-slate-500 hover:bg-slate-100 rounded-xl flex-shrink-0"
                            title="Adjuntar archivo"
                          >
                            <Paperclip className="w-5 h-5" />
                          </button>
                          <textarea
                            value={msgText}
                            onChange={e => setMsgText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={attachedFile ? "Caption (opcional)..." : "Escribe un mensaje..."}
                            rows={1}
                            className="flex-1 resize-none border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 max-h-32"
                            style={{ minHeight: '42px' }}
                          />
                          <button
                            onClick={handleSend}
                            disabled={(!msgText.trim() && !attachedFile) || sending || uploadingFile}
                            className="p-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                          >
                            {uploadingFile ? <Clock className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Contact panel */}
                  {showContact && selectedConv && (
                    <ContactPanel phone={selectedConv.contact_phone} onClose={() => setShowContact(false)} />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Interactive message modal */}
      {showInteractive && (
        <InteractiveModal onClose={() => setShowInteractive(false)} onSend={handleSendInteractive} />
      )}
    </DashboardLayout>
  );
};

export default WhatsAppInbox;
